import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Inbox, Calendar, Reply, ShieldCheck, KeyRound, Trash2, Bell, BellOff, Copy } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AdminTools } from "@/components/AdminTools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AreaRiservata() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [authError, setAuthError] = useState(!!location.state?.authError);
  const [user, setUser] = useState(location.state?.user || null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => Number(localStorage.getItem("ar_last_seen") || 0));
  const [selected, setSelected] = useState(new Set());
  const [totpCode, setTotpCode] = useState("");
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpBusy, setTotpBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [attPwStatus, setAttPwStatus] = useState(null);
  const [attPw, setAttPw] = useState("");
  const [attPwBusy, setAttPwBusy] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [notifOn, setNotifOn] = useState(
    () => typeof Notification !== "undefined" && Notification.permission === "granted" && localStorage.getItem("ar_notif") === "1"
  );
  const lastMsgTs = useRef(0);

  const newCount = messages.filter((m) => new Date(m.timestamp).getTime() > lastSeen).length;
  const markAllRead = () => {
    const now = Date.now();
    localStorage.setItem("ar_last_seen", String(now));
    setLastSeen(now);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => axios.delete(`${API}/admin/contact/${id}`, { withCredentials: true })));
      setMessages((prev) => prev.filter((m) => !selected.has(m.id)));
      setSelected(new Set());
      toast.success(`${ids.length} messaggi eliminati`);
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const verifyTotp = async (e) => {
    e.preventDefault();
    setTotpBusy(true);
    try {
      await axios.post(`${API}/auth/totp/verify`, { code: totpCode }, { withCredentials: true });
      setUser({ ...user, totp_required: false });
      setTotpCode("");
      toast.success("Verifica completata");
    } catch {
      toast.error("Codice non valido, riprova");
    } finally {
      setTotpBusy(false);
    }
  };

  const startTotpSetup = async () => {
    setTotpBusy(true);
    try {
      const res = await axios.post(`${API}/auth/totp/setup`, {}, { withCredentials: true });
      setTotpSetup(res.data);
    } catch {
      toast.error("Impossibile avviare la configurazione");
    } finally {
      setTotpBusy(false);
    }
  };

  const rotateTotp = async () => {
    setTotpBusy(true);
    try {
      const res = await axios.post(`${API}/auth/totp/rotate`, {}, { withCredentials: true });
      setTotpSetup(res.data);
    } catch {
      toast.error("Impossibile rigenerare il QR code");
    } finally {
      setTotpBusy(false);
    }
  };

  const saveAttestatiPw = async (e) => {
    e.preventDefault();
    setAttPwBusy(true);
    try {
      await axios.post(`${API}/admin/attestati/password`, { password: attPw }, { withCredentials: true });
      toast.success("Password monouso aggiornata");
      setAttPw("");
      const res = await axios.get(`${API}/admin/attestati/password-status`, { withCredentials: true });
      setAttPwStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setAttPwBusy(false);
    }
  };

  const enableTotp = async (e) => {
    e.preventDefault();
    setTotpBusy(true);
    try {
      const res = await axios.post(`${API}/auth/totp/enable`, { code: totpCode }, { withCredentials: true });
      setUser({ ...user, totp_enabled: true, backup_codes_left: 8 });
      setTotpSetup(null);
      setTotpCode("");
      setBackupCodes(res.data.backup_codes || null);
      toast.success("Verifica in due passaggi attivata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido, riprova");
    } finally {
      setTotpBusy(false);
    }
  };

  const regenBackupCodes = async () => {
    setTotpBusy(true);
    try {
      const res = await axios.post(`${API}/auth/totp/backup-codes`, {}, { withCredentials: true });
      setBackupCodes(res.data.backup_codes);
      setUser({ ...user, backup_codes_left: 8 });
      toast.success("Nuovi codici di riserva generati — i vecchi non funzionano più");
    } catch {
      toast.error("Rigenerazione non riuscita");
    } finally {
      setTotpBusy(false);
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      toast.success("Codici copiati — conservali in un posto sicuro");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const toggleNotif = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Questo browser non supporta le notifiche");
      return;
    }
    if (notifOn) {
      localStorage.setItem("ar_notif", "0");
      setNotifOn(false);
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      localStorage.setItem("ar_notif", "1");
      setNotifOn(true);
      toast.success("Notifiche attive: ti avviso quando arriva un nuovo messaggio");
    } else {
      toast.error("Permesso notifiche negato dal browser");
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        setAuth(true);
      })
      .catch(() => setAuth(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth !== true || !user || user.totp_required) return;
    setLoadingMsgs(true);
    axios
      .get(`${API}/contact`, { withCredentials: true })
      .then((res) => {
        const list = [...res.data].reverse();
        setMessages(list);
        lastMsgTs.current = list.length ? new Date(list[0].timestamp).getTime() : 0;
      })
      .catch(() => {})
      .finally(() => setLoadingMsgs(false));
  }, [auth, user]);

  useEffect(() => {
    if (auth !== true || !user || user.totp_required) return;
    axios.get(`${API}/admin/attestati/password-status`, { withCredentials: true })
      .then((res) => setAttPwStatus(res.data))
      .catch(() => {});
  }, [auth, user]);

  useEffect(() => {
    if (auth !== true || !user || user.totp_required) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/contact`, { withCredentials: true });
        const list = [...res.data].reverse();
        const latest = list.length ? new Date(list[0].timestamp).getTime() : 0;
        if (lastMsgTs.current && latest > lastMsgTs.current) {
          setMessages(list);
          if (
            localStorage.getItem("ar_notif") === "1" &&
            typeof Notification !== "undefined" &&
            Notification.permission === "granted"
          ) {
            new Notification("Nuovo messaggio dal sito", {
              body: `${list[0].name}: ${(list[0].message || "").slice(0, 80)}`,
            });
          }
        }
        if (latest > lastMsgTs.current) lastMsgTs.current = latest;
      } catch {}
    };
    const iv = setInterval(tick, 30000);
    return () => clearInterval(iv);
  }, [auth, user]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/area-riservata";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuth(false);
    setUser(null);
    setMessages([]);
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword }, { withCredentials: true });
      setUser(res.data);
      setAuth(true);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Accesso non riuscito: controlla email e password.");
    } finally {
      setLoginBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#DCEEF5] bg-[#F8FBFD] px-4 py-3 text-sm text-[#0B2530] outline-none focus:border-[#06B6D4] focus:ring-2 focus:ring-cyan-100 transition";

  return (
    <main className="min-h-screen flex flex-col" data-testid="area-riservata-page">
      <Navbar />
      <section className="flex-1 pt-32 pb-24 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <span className="font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">Accesso riservato</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0B2530] mt-4">
            Area <em className="font-serif-ed italic font-medium text-[#06B6D4]">Riservata</em>
          </h1>

          {auth === null && (
            <div className="mt-16 text-center text-[#5F7E8A]" data-testid="area-riservata-loading">
              Verifica della sessione in corso…
            </div>
          )}

          {auth === false && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 rounded-3xl bg-white border border-[#DCEEF5] p-10 sm:p-14 text-center max-w-lg mx-auto shadow-xl shadow-sky-500/5"
            >
              <div className="w-14 h-14 rounded-full bg-[#ECFEFF] border border-[#A5F3FC] flex items-center justify-center mx-auto">
                <Lock size={22} className="text-[#0284C7]" />
              </div>
              <h2 className="font-display text-xl font-bold text-[#0B2530] mt-6">Solo per Yuri</h2>
              <p className="text-sm text-[#3D5A66] mt-3 leading-relaxed">
                Quest'area contiene i messaggi ricevuti dal form di contatto. Accedi con l'account Google autorizzato.
              </p>
              {authError && (
                <p className="mt-4 text-sm font-medium text-[#C2410C]" data-testid="area-riservata-auth-error">
                  Accesso non riuscito: account non autorizzato o sessione scaduta.
                </p>
              )}
              <button onClick={login} data-testid="google-login-button"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#0B2530] text-white font-semibold px-8 py-3.5 hover:bg-[#0284C7] transition-colors duration-300 active:scale-95">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/>
                </svg>
                Accedi con Google
              </button>
              <div className="mt-8 pt-8 border-t border-[#DCEEF5] text-left">
                <p className="text-xs font-mono-ed uppercase tracking-[0.14em] text-[#5F7E8A] text-center">Accesso di riserva</p>
                <form onSubmit={passwordLogin} className="mt-4 space-y-3" data-testid="password-login-form">
                  <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required
                    placeholder="Email" autoComplete="email" data-testid="password-login-email" className={inputCls} />
                  <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required
                    placeholder="Password" autoComplete="current-password" data-testid="password-login-password" className={inputCls} />
                  {loginError && (
                    <p className="text-xs font-medium text-[#C2410C]" data-testid="password-login-error">{loginError}</p>
                  )}
                  <button type="submit" disabled={loginBusy || !loginEmail.trim() || !loginPassword} data-testid="password-login-button"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 disabled:opacity-60">
                    {loginBusy ? "Accesso in corso..." : "Accedi con email e password"}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {auth === true && user?.totp_required && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 rounded-3xl bg-white border border-[#DCEEF5] p-10 sm:p-14 text-center max-w-lg mx-auto shadow-xl shadow-sky-500/5"
              data-testid="totp-gate"
            >
              <div className="w-14 h-14 rounded-full bg-[#FDE7D3] border border-[#F0A05C] flex items-center justify-center mx-auto">
                <KeyRound size={22} className="text-[#C2410C]" />
              </div>
              <h2 className="font-display text-xl font-bold text-[#0B2530] mt-6">Verifica in due passaggi</h2>
              <p className="text-sm text-[#3D5A66] mt-3 leading-relaxed">
                Apri l'app Authenticator sul telefono e inserisci il codice a 6 cifre per completare l'accesso.
                Senza telefono? Incolla uno dei tuoi codici di riserva monouso.
              </p>
              <form onSubmit={verifyTotp} className="mt-8 space-y-4">
                <input
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  required
                  data-testid="totp-code-input"
                  className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono-ed`}
                />
                <button type="submit" disabled={totpBusy || totpCode.length !== 6} data-testid="totp-verify-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3.5 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 disabled:opacity-60">
                  {totpBusy ? "Verifica..." : "Verifica e accedi"}
                </button>
              </form>
              <button onClick={logout} data-testid="totp-gate-logout" className="mt-5 text-xs font-medium text-[#5F7E8A] hover:text-[#C2410C] transition-colors">
                Non sei tu? Esci
              </button>
            </motion.div>
          )}

          {auth === true && user && !user.totp_required && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white border border-[#DCEEF5] px-6 py-4">
                <div className="flex items-center gap-3">
                  {user?.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />}
                  <div>
                    <div className="text-sm font-semibold text-[#0B2530]" data-testid="area-riservata-user-name">{user?.name}</div>
                    <div className="text-xs text-[#5F7E8A]">{user?.email}</div>
                  </div>
                </div>
                <button onClick={logout} data-testid="logout-button"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#3D5A66] hover:text-[#C2410C] transition-colors">
                  <LogOut size={15} /> Esci
                </button>
              </div>

              <div className="mt-6 rounded-2xl bg-white border border-[#DCEEF5] p-6" data-testid="twofa-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={20} className={user.totp_enabled ? "text-[#16A34A]" : "text-[#E8894B]"} />
                    <div>
                      <div className="text-sm font-semibold text-[#0B2530]">Verifica in due passaggi (2FA)</div>
                      <div className="text-xs text-[#5F7E8A]">
                        {user.totp_enabled
                          ? "Attiva: a ogni accesso serve il codice dell'app Authenticator"
                          : "Non attiva: aggiungi un codice Authenticator oltre all'accesso Google"}
                      </div>
                    </div>
                  </div>
                  {user.totp_enabled ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs text-[#5F7E8A]" data-testid="backup-codes-count">{user.backup_codes_left ?? 0} codici di riserva rimasti</span>
                      {!totpSetup && (
                        <button onClick={rotateTotp} disabled={totpBusy} data-testid="twofa-rotate-button"
                          className="rounded-full border border-[#DCEEF5] text-[#3D5A66] text-xs font-semibold px-4 py-2 hover:border-[#0284C7] hover:text-[#0284C7] transition-colors active:scale-95 disabled:opacity-60">
                          {totpBusy ? "Preparazione..." : "Rigenera QR"}
                        </button>
                      )}
                      <button onClick={regenBackupCodes} disabled={totpBusy} data-testid="twofa-regen-button"
                        className="rounded-full border border-[#DCEEF5] text-[#3D5A66] text-xs font-semibold px-4 py-2 hover:border-[#0284C7] hover:text-[#0284C7] transition-colors active:scale-95 disabled:opacity-60">
                        Rigenera codici
                      </button>
                      <span className="rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2" data-testid="twofa-status-on">Attiva</span>
                    </div>
                  ) : (
                    !totpSetup && (
                      <button onClick={startTotpSetup} disabled={totpBusy} data-testid="twofa-setup-button"
                        className="rounded-full bg-[#0B2530] text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#0284C7] transition-colors active:scale-95 disabled:opacity-60">
                        {totpBusy ? "Preparazione..." : "Attiva 2FA"}
                      </button>
                    )
                  )}
                </div>
                {totpSetup && (
                  <div className="mt-6 border-t border-[#DCEEF5] pt-6 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-start" data-testid="twofa-setup-panel">
                    <img src={totpSetup.qr} alt="QR code per Google Authenticator" className="w-40 h-40 rounded-xl border border-[#DCEEF5]" data-testid="twofa-qr" />
                    <div>
                      <p className="text-sm text-[#3D5A66] leading-relaxed">
                        1. Apri <strong>Google Authenticator</strong> (o un'altra app TOTP) e scansiona il QR code.<br />
                        2. In alternativa inserisci manualmente questa chiave:
                      </p>
                      {user.totp_enabled && (
                        <p className="text-xs text-[#C2410C] mt-2 leading-relaxed" data-testid="twofa-rotate-warning">
                          Stai rigenerando il codice: dopo la conferma il vecchio codice Authenticator smetterà di funzionare e riceverai nuovi codici di riserva.
                        </p>
                      )}
                      <code className="block mt-2 rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2 text-xs font-mono-ed text-[#0369A1] break-all select-all" data-testid="twofa-secret">
                        {totpSetup.secret}
                      </code>
                      <form onSubmit={enableTotp} className="mt-4 flex gap-3">
                        <input
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          inputMode="numeric"
                          placeholder="Codice a 6 cifre"
                          required
                          data-testid="twofa-enable-input"
                          className={inputCls}
                        />
                        <button type="submit" disabled={totpBusy || totpCode.length !== 6} data-testid="twofa-enable-button"
                          className="shrink-0 rounded-full bg-[#0284C7] text-white text-sm font-semibold px-6 py-3 hover:bg-[#075E8A] transition-colors active:scale-95 disabled:opacity-60">
                          Attiva
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {backupCodes && (
                  <div className="mt-6 border-t border-[#DCEEF5] pt-6" data-testid="backup-codes-box">
                    <p className="text-sm font-semibold text-[#0B2530]">Codici di riserva monouso</p>
                    <p className="text-xs text-[#C2410C] mt-1 leading-relaxed">
                      Conservali subito in un posto sicuro: se perdi il telefono, ognuno di questi codici ti fa entrare una volta al posto di Authenticator. Non verranno mostrati di nuovo.
                    </p>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {backupCodes.map((c) => (
                        <code key={c} className="rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2 text-center text-sm font-mono-ed text-[#0369A1] select-all">{c}</code>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button onClick={copyBackupCodes} data-testid="backup-codes-copy"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2530] text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#0284C7] transition-colors active:scale-95">
                        <Copy size={13} /> Copia i codici
                      </button>
                      <button onClick={() => setBackupCodes(null)} data-testid="backup-codes-done"
                        className="text-xs font-semibold text-[#5F7E8A] hover:text-[#0B2530] transition-colors px-3">
                        Li ho conservati
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 rounded-2xl bg-white border border-[#DCEEF5] p-6" data-testid="attestati-pw-card">
                <div className="flex items-center gap-3">
                  <KeyRound size={20} className={attPwStatus?.configured && !attPwStatus?.used ? "text-[#16A34A]" : "text-[#E8894B]"} />
                  <div>
                    <div className="text-sm font-semibold text-[#0B2530]">Password pagina Attestati (monouso)</div>
                    <div className="text-xs text-[#5F7E8A]" data-testid="attestati-pw-status">
                      {!attPwStatus?.configured ? "Non configurata: la pagina è bloccata per tutti" : attPwStatus.used ? "Già utilizzata — generane una nuova" : "Attiva e mai utilizzata"}
                    </div>
                  </div>
                </div>
                <form onSubmit={saveAttestatiPw} className="mt-4 flex flex-wrap items-center gap-3">
                  <input type="text" value={attPw} onChange={(e) => setAttPw(e.target.value)} data-testid="attestati-pw-input"
                    placeholder="Nuova password monouso (min. 6 caratteri)" autoComplete="off"
                    className="flex-1 min-w-[220px] rounded-xl border border-[#DCEEF5] bg-[#FBFEFF] px-4 py-3 text-sm text-[#0B2530] focus:outline-none focus:border-[#0284C7] transition-colors" />
                  <button type="submit" disabled={attPwBusy || attPw.trim().length < 6} data-testid="attestati-pw-save"
                    className="rounded-full bg-[#0B2530] text-white text-xs font-semibold px-5 py-3 hover:bg-[#0284C7] transition-colors active:scale-95 disabled:opacity-60">
                    {attPwBusy ? "Salvataggio..." : "Imposta / Rigenera"}
                  </button>
                </form>
                <p className="text-xs text-[#5F7E8A] mt-3 leading-relaxed">
                  La password è monouso: chi la inserisce sblocca la pagina Attestati una sola volta, poi non è più valida. Condividila solo con chi vuoi far entrare e rigenerala da qui quando serve.
                </p>
              </div>

              <div className="mt-10 mb-5 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-xl font-bold text-[#0B2530] flex items-center gap-2">
                  <Inbox size={19} className="text-[#06B6D4]" /> Messaggi ricevuti
                  <span className="text-sm font-medium text-[#5F7E8A]" data-testid="messages-count">({messages.length})</span>
                  {newCount > 0 && (
                    <span className="rounded-full bg-[#E8894B] text-white text-xs font-bold px-2.5 py-1" data-testid="new-messages-badge">
                      {newCount} nuov{newCount === 1 ? "o" : "i"}
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-4">
                  <button onClick={toggleNotif} data-testid="notif-toggle-button"
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${notifOn ? "text-[#16A34A] hover:text-[#15803D]" : "text-[#5F7E8A] hover:text-[#0284C7]"}`}>
                    {notifOn ? <Bell size={14} /> : <BellOff size={14} />}
                    {notifOn ? "Notifiche attive" : "Attiva notifiche"}
                  </button>
                  {selected.size > 0 && (
                    <button onClick={deleteSelected} data-testid="delete-selected-button"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#C2410C] text-white text-xs font-semibold px-4 py-2 hover:bg-[#9A3412] transition-colors active:scale-95">
                      <Trash2 size={13} /> Elimina selezionati ({selected.size})
                    </button>
                  )}
                  {newCount > 0 && (
                    <button onClick={markAllRead} data-testid="mark-read-button"
                      className="text-xs font-semibold text-[#0284C7] hover:text-[#075E8A] transition-colors">
                      Segna tutti come letti
                    </button>
                  )}
                </div>
              </div>

              {loadingMsgs && <div className="text-sm text-[#5F7E8A]">Caricamento messaggi…</div>}

              {!loadingMsgs && messages.length === 0 && (
                <div className="rounded-2xl border border-dashed border-[#BAE6FD] bg-[#F0F9FF] p-10 text-center text-[#5F7E8A] text-sm" data-testid="messages-empty">
                  Nessun messaggio per ora: quelli inviati dal form di contatto appariranno qui.
                </div>
              )}

              <div className="space-y-4" data-testid="messages-list">
                {messages.map((m) => {
                  const isNew = new Date(m.timestamp).getTime() > lastSeen;
                  const isSelected = selected.has(m.id);
                  return (
                    <article key={m.id}
                      className={`rounded-2xl bg-white border p-6 transition-colors duration-300 ${isSelected ? "border-[#0284C7] shadow-md shadow-sky-100" : isNew ? "border-[#F0A05C] shadow-md shadow-orange-100" : "border-[#DCEEF5]"}`}
                      data-testid={`message-${m.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(m.id)}
                            data-testid={`message-select-${m.id}`}
                            aria-label={`Seleziona messaggio di ${m.name}`}
                            className="w-4 h-4 rounded border-[#BAE6FD] text-[#0284C7] accent-[#0284C7] cursor-pointer"
                          />
                          <Mail size={15} className="text-[#06B6D4]" />
                          <span className="font-semibold text-[#0B2530] text-sm">{m.name}</span>
                          <a href={`mailto:${m.email}`} className="text-xs text-[#0284C7] hover:underline">{m.email}</a>
                          {isNew && <span className="rounded-full bg-[#FDE7D3] text-[#C2410C] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">Nuovo</span>}
                        </div>
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#5F7E8A]">
                          <Calendar size={12} />
                          {new Date(m.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-sm text-[#3D5A66] leading-relaxed mt-3 whitespace-pre-line">{m.message}</p>
                      <div className="mt-4 flex justify-end">
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent("Re: il tuo messaggio dal sito")}&body=${encodeURIComponent(`Ciao ${m.name},\n\ngrazie per avermi scritto. \n\nIn riferimento al tuo messaggio:\n"${m.message.slice(0, 300)}"\n\n— Yuri Selvaggio\ninfo@yurjselvaggio.it\n+355 (0) 694524787`)}`}
                          data-testid={`reply-button-${m.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#BAE6FD] text-[#0284C7] text-xs font-semibold px-4 py-2 hover:bg-[#0284C7] hover:text-white hover:border-[#0284C7] transition-colors duration-300 active:scale-95"
                        >
                          <Reply size={13} /> Rispondi
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>

              <AdminTools />
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
